import { FunctionComponent } from 'react';
import { Button, IconButton, LinearProgress, Dialog, Table, TableRow, TableCell } from '../../../../tailwindcomponents';
import { Part } from '../../../../types/tasks';
import { redirectToMxi } from '../../../../config/mxiProvider';
import { Link } from '../../../icons';

const EXPORT_COLUMNS: Array<[string, (p: Part) => unknown]> = [
  ['Tail', p => p.tail_number],
  ['Location', p => p.wo_location],
  ['Req', p => p.REQ],
  ['PN', p => p.PN],
  ['Description', p => p.PartDescription],
  ['Qty', p => p.REQ_QT],
  ['Status', p => p.Status],
  ['PO', p => p.po_number],
  ['ETA', p => p.EST_ARRIVAL_DT],
  ['Start date', p => p.scheduled_start_date_text],
  ['Note', p => p.req_note],
];

// CSV with BOM and ';' delimiter, opens directly in Excel (same approach as the certificates export)
const exportToExcel = (parts: Array<Part>) => {
  const escape = (v: unknown) => {
    const s = String(v ?? '');
    // Free text (notes, descriptions) starting with =, +, -, @, tab or CR would be read as a formula by Excel
    const safe = typeof v === 'string' ? s.replace(/^[=+\-@\t\r]/, "'$&") : s;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  const rows = [
    EXPORT_COLUMNS.map(([header]) => escape(header)).join(';'),
    ...parts.map(p => EXPORT_COLUMNS.map(([, get]) => escape(get(p))).join(';')),
  ];
  const blob = new Blob(['﻿' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'Unavailable parts.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

type OutstandingPartsDialogProps = {
  visible: boolean;
  parts: Array<Part>;
  loading: boolean;
  onClose: () => void;
};

const OutstandingPartsDialog: FunctionComponent<OutstandingPartsDialogProps> = ({
  visible,
  parts,
  loading,
  onClose,
}) => {
  const generateOutstandingPartsTableBody = (row: Part, index: number) => {
    return (
      <TableRow key={index}>
        <TableCell></TableCell>
        <TableCell>{row.tail_number}</TableCell>
        <TableCell>{row.wo_location}</TableCell>
        <TableCell onClick={() => redirectToMxi(row.wo_barcode)}>
          <IconButton><Link color="primary" /></IconButton>
        </TableCell>
        <TableCell>{row.REQ}</TableCell>
        <TableCell>{row.PN}</TableCell>
        <TableCell>{row.PartDescription}</TableCell>
        <TableCell>{row.REQ_QT}</TableCell>
        <TableCell>{row.Status}</TableCell>
        <TableCell>{row.po_number}</TableCell>
        <TableCell>{row.EST_ARRIVAL_DT}</TableCell>
        <TableCell>{row.scheduled_start_date_text}</TableCell>
        <TableCell>{row.req_note}</TableCell>
      </TableRow>
    );
  };

  return (
    <Dialog
      visible={visible}
      max_width={"xl"}
      title={"Unavailable parts"}
      context={
        <div>
          {loading === true && (
            <div>
              <br />
              <LinearProgress />
            </div>
          )}
          <div className="flex justify-end mb-2">
            <Button variant="outlined" color="primary" disabled={loading || parts.length === 0} onClick={() => exportToExcel(parts)}>
              Export to Excel
            </Button>
          </div>
          <Table
            headers={["Tail", "Location", "Mxi", "Req", "PN", "Description", "Qty", "Status", "PO", "ETA", "Start date", "Note"]}
            rows={parts}
            generateBody={(row: Part, index: number) => generateOutstandingPartsTableBody(row, index)}
          />
        </div>
      }
      onClose={() => onClose()}
      strict={false}
      show_options={false}
    />
  );
};

export default OutstandingPartsDialog;
